import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  ParametresTraverses,
  StructureTraverses,
  TypeMotifTraverse,
  TypeMateriauTraverse,
  TraversesState,
  MotifPersonnalise,
  DonneesStructureBase
} from '../types';
import { TraversesAPI } from '../api/TraversesAPI';
import { Traverses3DService } from '../services/Traverses3DService';
import { ModalEditeur2D } from './ModalEditeur2D';
import './TraversesModule.css';

interface TraversesModuleProps {
  donneesStructureBase: DonneesStructureBase;
  onTraversesChange?: (structure: StructureTraverses | null) => void;
  onError?: (error: string) => void;
}

/**
 * Module principal pour la gestion des traverses
 * Composant React responsable de l'interface utilisateur et de la coordination des services
 */
export const TraversesModule: React.FC<TraversesModuleProps> = ({
  donneesStructureBase,
  onTraversesChange,
  onError
}) => {
  // États du composant
  const [state, setState] = useState<TraversesState>({
    parametres: {
      actif: false,
      motif: TypeMotifTraverse.SIMPLE,
      motifPersonnalise: undefined,
      nombreRangees: 1,
      materiau: TypeMateriauTraverse.FER_A_BETON,
      espacementVertical: 0.5,
      hauteurPosition: 0.5,
      epaisseur: 0.05,
      couleur: '#8B4513'
    },
    isGenerating: false,
    canvasActif: false
  });



  const [motifsPersonnalises, setMotifsPersonnalises] = useState<MotifPersonnalise[]>([]);
  const [motifSelectionne, setMotifSelectionne] = useState<MotifPersonnalise | null>(null);
  const [modalEditeurOuverte, setModalEditeurOuverte] = useState(false);

  // Références
  const apiRef = useRef<TraversesAPI>(new TraversesAPI());
  const serviceRef = useRef<Traverses3DService | null>(null);
  
  // Mémorisation des valeurs pour éviter les re-rendus
  const nombreMontants = useMemo(() => donneesStructureBase?.nombreMontants, [donneesStructureBase]);
  const nombrePointsPoutre = useMemo(() => donneesStructureBase?.poutreHaute?.points?.length, [donneesStructureBase]);

  // Synchronisation avec la structure de base
  useEffect(() => {
    if (donneesStructureBase) {
      // Convertir le matériau de la structure de base vers le type traverses
      const materiauTraverse = donneesStructureBase.materiau === 'tube_carre' 
        ? TypeMateriauTraverse.TUBE_CARRE 
        : TypeMateriauTraverse.FER_A_BETON;
      
      // Synchroniser l'épaisseur et la couleur des traverses avec celle de la structure de base
      // Conversion de mm (structure de base) vers mètres (traverses)
      const epaisseurStructureEnMetres = donneesStructureBase.epaisseur / 1000;
      
      // Utiliser directement l'épaisseur et la couleur de la structure de base
      const epaisseurFinale = epaisseurStructureEnMetres;
      const couleurGlobale = donneesStructureBase.couleur || '#8B4513';
      
      setState(prevState => ({
        ...prevState,
        parametres: {
          ...prevState.parametres,
          materiau: materiauTraverse,
          epaisseur: epaisseurFinale,
          couleur: couleurGlobale
        }
      }));
    }
  }, [donneesStructureBase?.materiau, donneesStructureBase?.epaisseur, donneesStructureBase?.couleur]);

  // Initialisation des services
  useEffect(() => {
    if (!serviceRef.current) {
      serviceRef.current = new Traverses3DService();
    }

    return () => {
      if (serviceRef.current) {
        serviceRef.current.dispose();
        serviceRef.current = null;
      }
    };
  }, []);



  // Fonction pour recharger les motifs depuis le serveur
  const rechargerMotifs = useCallback(async () => {
    try {
      const motifsData = await apiRef.current.obtenirMotifsPersonnalises();
      setMotifsPersonnalises(motifsData);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    }
  }, []);

  // Chargement des motifs au démarrage
  useEffect(() => {
    rechargerMotifs();
  }, [rechargerMotifs]);

  /**
   * Génère les traverses avec les paramètres actuels
   */
  // Références pour les callbacks pour éviter les dépendances changeantes
  const onTraversesChangeRef = useRef(onTraversesChange);
  const onErrorRef = useRef(onError);
  
  // Mettre à jour les références quand les props changent
  useEffect(() => {
    onTraversesChangeRef.current = onTraversesChange;
    onErrorRef.current = onError;
  });

  const genererTraverses = useCallback(async (parametresAUtiliser: ParametresTraverses) => {
    if (!serviceRef.current) return;



    setState(prev => ({ ...prev, isGenerating: true, error: undefined }));

    try {
        // Validation supprimée - plus de contraintes

      // Génération 3D
      const structure = serviceRef.current.genererTraverses(
        parametresAUtiliser,
        donneesStructureBase
      );



      setState(prev => ({ ...prev, structure, isGenerating: false }));
      onTraversesChangeRef.current?.(structure);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      console.error('❌ [TraversesModule] Erreur génération:', errorMessage);

      setState(prev => ({ ...prev, error: errorMessage, isGenerating: false }));
      onErrorRef.current?.(errorMessage);
    }
  }, [donneesStructureBase]);

  // Référence pour éviter les boucles infinies
  const isGeneratingRef = useRef(false);

  // Référence pour les paramètres précédents et données structure
  const previousParametresRef = useRef<ParametresTraverses | null>(null);
  const previousStructureRef = useRef<any>(null);

  // Génération automatique lors des changements de paramètres ou de structure
  useEffect(() => {
    // Éviter les boucles infinies
    if (isGeneratingRef.current) {
      return;
    }
    
    // Vérifier si les paramètres ont réellement changé
    const currentParams = state.parametres;
    const previousParams = previousParametresRef.current;
    const previousStructure = previousStructureRef.current;
    
    // Vérifier changements paramètres
    const parametresChanged = !previousParams || 
        previousParams.actif !== currentParams.actif ||
        previousParams.motif !== currentParams.motif ||
        previousParams.nombreRangees !== currentParams.nombreRangees ||
        previousParams.espacementVertical !== currentParams.espacementVertical ||
        previousParams.hauteurPosition !== currentParams.hauteurPosition ||
        previousParams.epaisseur !== currentParams.epaisseur ||
        previousParams.couleur !== currentParams.couleur ||
        previousParams.materiau !== currentParams.materiau;
    
    // Vérifier changements structure de base
    const structureChanged = !previousStructure ||
        previousStructure.largeur !== donneesStructureBase.largeur ||
        previousStructure.longueur !== donneesStructureBase.longueur ||
        previousStructure.hauteur !== donneesStructureBase.hauteur ||
        previousStructure.materiau !== donneesStructureBase.materiau ||
        previousStructure.forme !== donneesStructureBase.forme ||
        previousStructure.nombreMontants !== donneesStructureBase.nombreMontants ||
        previousStructure.positionsMontants?.length !== donneesStructureBase.positionsMontants?.length;
    
    if (!parametresChanged && !structureChanged) {
      return; // Aucun changement réel
    }
    
    // Sauvegarder les paramètres et structure actuels
    previousParametresRef.current = { ...currentParams };
    previousStructureRef.current = { ...donneesStructureBase };
    
    if (currentParams.actif && serviceRef.current) {
      isGeneratingRef.current = true;
      genererTraverses(currentParams).finally(() => {
        isGeneratingRef.current = false;
      });
    } else if (!currentParams.actif && serviceRef.current) {
      // Nettoyer les traverses si désactivées
      onTraversesChangeRef.current?.(null);
    }
  }, [donneesStructureBase]);

  // Génération automatique lors des changements de paramètres utilisateur
  useEffect(() => {
    // Éviter les boucles infinies
    if (isGeneratingRef.current) {
      return;
    }
    
    const currentParams = state.parametres;
    const previousParams = previousParametresRef.current;
    
    // Vérifier changements paramètres utilisateur (pas la synchronisation automatique)
    const parametresChanged = previousParams && (
        previousParams.actif !== currentParams.actif ||
        previousParams.motif !== currentParams.motif ||
        previousParams.nombreRangees !== currentParams.nombreRangees ||
        previousParams.espacementVertical !== currentParams.espacementVertical ||
        previousParams.hauteurPosition !== currentParams.hauteurPosition ||
        previousParams.couleur !== currentParams.couleur ||
        previousParams.motifPersonnalise !== currentParams.motifPersonnalise
    );
    
    if (parametresChanged && currentParams.actif && serviceRef.current) {
      previousParametresRef.current = { ...currentParams };
      isGeneratingRef.current = true;
      genererTraverses(currentParams).finally(() => {
        isGeneratingRef.current = false;
      });
    } else if (parametresChanged && !currentParams.actif && serviceRef.current) {
      // Nettoyer les traverses si désactivées
      onTraversesChangeRef.current?.(null);
    }
  }, [state.parametres.actif, state.parametres.motif, state.parametres.nombreRangees, state.parametres.espacementVertical, state.parametres.hauteurPosition, state.parametres.couleur, state.parametres.motifPersonnalise]);

  /**
   * Met à jour un paramètre spécifique
   */
  const mettreAJourParametre = useCallback(<K extends keyof ParametresTraverses>(
    cle: K,
    valeur: ParametresTraverses[K]
  ) => {
    setState(prev => ({
      ...prev,
      parametres: {
        ...prev.parametres,
        [cle]: valeur
      }
    }));
  }, []);

  /**
   * Sauvegarde le motif personnalisé actuel
   */
  const sauvegarderMotifPersonnalise = useCallback(async (nom: string, motifData?: MotifPersonnalise) => {
    try {
      // Utiliser les données du motif si fournies, sinon créer un motif vide
      const motif: MotifPersonnalise = motifData ? {
        ...motifData,
        nom // S'assurer que le nom est correct
      } : {
        id: Date.now().toString(),
        nom,
        courbes: [],
        lignes: [],
        lignesDroites: [],
        largeur: 400,
        hauteur: 300
      };
      
      const resultat = await apiRef.current.sauvegarderMotifPersonnalise(motif);
      
      if (resultat.success && resultat.motif) {
        // Utiliser le motif retourné par le serveur avec l'ID correct
        const motifSauvegarde = resultat.motif;
        
        // Recharger la liste complète des motifs depuis le serveur
        // pour s'assurer que l'interface est synchronisée
        await rechargerMotifs();
        
        setMotifSelectionne(motifSauvegarde);
        
        // Ajouter un timestamp pour forcer le recalcul
        const motifAvecTimestamp = {
          ...motifSauvegarde,
          _lastUpdate: Date.now()
        };
        mettreAJourParametre('motifPersonnalise', motifAvecTimestamp);
      } else {
        throw new Error(resultat.error || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      onError?.(errorMessage);
    }
  }, [mettreAJourParametre, onError, rechargerMotifs]);

  /**
   * Charge un motif personnalisé
   */
  const chargerMotifPersonnalise = useCallback((motif: MotifPersonnalise) => {
    setMotifSelectionne(motif);
    mettreAJourParametre('motif', TypeMotifTraverse.PERSONNALISE);
    mettreAJourParametre('motifPersonnalise', motif);
  }, [mettreAJourParametre]);

  /**
   * Supprime un motif personnalisé
   */
  const supprimerMotifPersonnalise = useCallback(async (motifId: string) => {
    try {
      const resultat = await apiRef.current.supprimerMotifPersonnalise(motifId);
      if (resultat.success) {
        // Recharger la liste complète des motifs depuis le serveur
        await rechargerMotifs();
        
        if (motifSelectionne?.id === motifId) {
          setMotifSelectionne(null);
          mettreAJourParametre('motif', TypeMotifTraverse.SIMPLE);
          mettreAJourParametre('motifPersonnalise', undefined);
        }
      } else {
        throw new Error(resultat.error || 'Erreur lors de la suppression');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      onError?.(errorMessage);
    }
  }, [motifSelectionne, mettreAJourParametre, onError, rechargerMotifs]);

  // Styles en ligne pour correspondre au module soudures
  const controleGroupeStyles = {
    marginBottom: '8px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px'
  };

  const controleLabelStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#e0e0e0'
  };

  return (
    <div className="traverses-module">
      <div>
        <h3>🏗️ Module Traverses</h3>
        <p style={{ margin: '0 0 10px 0', color: '#aaa', fontSize: '12px' }}>Gestion des traverses horizontales de la tonnelle</p>
      </div>
      
      {/* Activation/Désactivation */}
      <div style={controleGroupeStyles}>
        <label style={controleLabelStyles}>
          <input
            type="checkbox"
            checked={state.parametres.actif}
            onChange={(e) => mettreAJourParametre('actif', e.target.checked)}
          />
          Activer les traverses
        </label>
      </div>

      {state.parametres.actif && (
        <>
          {/* Sélection du motif */}
          <div className="controle-groupe">
            <label className="controle-label">Motif des traverses :</label>
            <select
              value={state.parametres.motif}
              onChange={(e) => {
                const nouveauMotif = e.target.value as TypeMotifTraverse;
                mettreAJourParametre('motif', nouveauMotif);
                // Ouvrir automatiquement le canvas si motif personnalisé sélectionné
                if (nouveauMotif === TypeMotifTraverse.PERSONNALISE) {
                  setModalEditeurOuverte(true);
                }
              }}
              className="controle-select"
            >
              <option value={TypeMotifTraverse.SIMPLE}>Simple</option>
              <option value={TypeMotifTraverse.VAGUE_DOUCE}>Vague douce</option>
              <option value={TypeMotifTraverse.LOSANGE}>Losange</option>
              <option value={TypeMotifTraverse.PERSONNALISE}>Personnalisé</option>
            </select>
          </div>



          {/* Paramètres des traverses */}
          <div className="controle-groupe">
            <label className="controle-label">
              Nombre de rangées : {state.parametres.nombreRangees}
            </label>
            <input
              type="range"
              min="1"
              max="5"
              step={1}
              value={state.parametres.nombreRangees}
              onChange={(e) => mettreAJourParametre('nombreRangees', parseInt(e.target.value))}
              className="controle-slider"
            />
          </div>

          <div className="controle-groupe">
            <label className="controle-label">
              Espacement vertical : {state.parametres.espacementVertical.toFixed(2)}m
            </label>
            <input
              type="range"
              min="0.1"
              max="2.0"
              step={0.05}
              value={state.parametres.espacementVertical}
              onChange={(e) => mettreAJourParametre('espacementVertical', parseFloat(e.target.value))}
              className="controle-slider"
            />
          </div>

          <div className="controle-groupe">
            <label className="controle-label">
              Position verticale : {(state.parametres.hauteurPosition * 100).toFixed(0)}%
            </label>
            <input
              type="range"
              min="0.1"
              max="0.9"
              step={0.05}
              value={state.parametres.hauteurPosition}
              onChange={(e) => mettreAJourParametre('hauteurPosition', parseFloat(e.target.value))}
              className="controle-slider"
            />
          </div>



          {/* Éditeur 2D pour motifs personnalisés */}
          {state.parametres.motif === TypeMotifTraverse.PERSONNALISE && (
            <div className="editeur-section">
              <h4>Éditeur de motif personnalisé</h4>
              
              <div className="editeur-controles">
                <button
                  onClick={() => setModalEditeurOuverte(true)}
                  className="bouton-editeur"
                >
                  Ouvrir l'éditeur 2D
                </button>
              </div>


            </div>
          )}
        </>
      )}

      {/* Modale éditeur 2D */}
       <ModalEditeur2D
         key={`modal-editeur-${motifsPersonnalises.length}`}
         isOpen={modalEditeurOuverte}
         onClose={() => setModalEditeurOuverte(false)}
         onSauvegarder={sauvegarderMotifPersonnalise}
         motifsPersonnalises={motifsPersonnalises}
         onChargerMotif={chargerMotifPersonnalise}
         onSupprimerMotif={supprimerMotifPersonnalise}
       />

      {/* Messages d'état */}
      <div className="statusZone" style={{ minHeight: '40px', display: 'flex', alignItems: 'center' }}>
        {state.error ? (
          <div className="message-erreur">Erreur : {state.error}</div>
        ) : null}
      </div>


    </div>
  );
};